import type { ContractFile } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeepPartial } from 'ts-essentials';

import { payloadClient } from '.';
import { contractKeys } from './contracts';
import type { FindPayloadApiClientParams } from './types';

export const contractFileKeys = {
  all: ['contract-files'] as const,
  lists: () => [...contractFileKeys.all, 'list'] as const,
  list: (params: ContractFileQueryParams) => [...contractFileKeys.lists(), params] as const,
} as const;

export type ContractFileQueryParams = FindPayloadApiClientParams<'contract-files', ContractFile> & {
  disabled?: boolean;
};

export const useContractFiles = (params?: ContractFileQueryParams) => {
  return useQuery({
    queryKey: contractFileKeys.list(params ?? {}),
    enabled: !params?.disabled,
    placeholderData: (previous) => previous,
    queryFn: async () =>
      payloadClient.find({
        collection: 'contract-files',
        ...params,
      }),
  });
};

export const useUploadContractFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, data }: { file: File; data: DeepPartial<ContractFile> }) =>
      payloadClient.create({
        collection: 'contract-files',
        data,
        file,
      }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: contractFileKeys.all });
      const contractId =
        typeof variables.data.contract === 'string'
          ? variables.data.contract
          : (variables.data.contract as { id?: string } | undefined)?.id;
      if (contractId) {
        queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
      }
    },
  });
};

export const useDeleteContractFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) =>
      payloadClient.deleteById({
        collection: 'contract-files',
        id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractFileKeys.all });
    },
  });
};
